import { Component, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { StoreIdService } from '../../core/services/store-id.service';
import { OrderService } from '../../core/services/order.service';
import { LoyaltyService } from '../../core/services/loyalty.service';
import { ConfigService } from '../../core/services/config.service';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { ModalName, ModalService } from '../../core/services/modal.service';
import { CommonModule } from '@angular/common';
import { AuditService } from '../../core/services/audit.service';

@Component({
  selector: 'cwo-loyalty-indicator',
  standalone: true,
  imports: [RouterLink, TranslocoDirective, CommonModule],
  templateUrl: './loyalty-indicator.component.html',
  styleUrl: './loyalty-indicator.component.scss'
})
export class LoyaltyIndicatorComponent {
  storeIdService = inject(StoreIdService);
  modalService = inject(ModalService);
  order = inject(OrderService).order;
  loyaltyPlan = inject(ConfigService).loyaltyState;
  membership = inject(LoyaltyService).loayaltyMembership;
  private auditService = inject(AuditService);
  private transloco = inject(TranslocoService);


  isInCheckout = input(false);

  closeModal() {
    this.auditService.createAudit(() => "Join to loyalty program clicked from the checkout");
    this.modalService.closeModal(ModalName.Cart);
  }

  get hasNextAwardPoints() {
    return isFinite(this.nextAwardPoints);
  }

  get stepRemainingPoints(): number {
    if (this.progressMaxPoints === 0) {
      return 0;
    }
    return this.progressMaxPoints - this.progressStepPoints;
  }

  get currentProgress(): number {
    if (this.progressMaxPoints === 0) {
      return 100;
    }
    return parseFloat(((100 * this.progressStepPoints) / (this.progressMaxPoints * 1.0)).toFixed(2));
  }

  
  private get progressStepPoints(): number {
    if (!this.membership()) {
      return 0;
    }
    return this.membership()!.availablePoints + this.order().loyaltyPoints - this.previousAwardPoints;
  }

  private get progressMaxPoints(): number {
    if (this.nextAwardPoints === 0) {
      // Needed to resolve an Infinity problem
      return 0;
    }
    return this.nextAwardPoints - this.previousAwardPoints;
  }

  private get previousAwardPoints(): number {
    if (!this.loyaltyPlan() || !this.loyaltyPlan()?.awards || !this.membership()) {
      return 0;
    }
    return Math.max(0, ...this.loyaltyPlan()!.awards.map(award => award.amount).filter(amount => amount <= (this.membership()!.availablePoints)));
  }

  private get nextAwardPoints(): number {
    if (!this.loyaltyPlan() || !this.loyaltyPlan()?.awards || !this.membership()) {
      return 0;
    }

    return Math.min(...this.loyaltyPlan()!.awards.map(award => award.amount).filter(amount => amount > this.membership()!.availablePoints)) || 0;
  }

  
  get orderPointsProgress(): number {
    if (this.progressMaxPoints === 0) {
      return 100;
    }
    let progress = parseFloat(((100 * this.order().loyaltyPoints) / (this.progressMaxPoints * 1.0)).toFixed(2));
    if (this.currentProgress + progress > 100.0) {
      progress = parseFloat((100.0 - this.currentProgress).toFixed(2));
    }
    return progress;
  }

   get loyaltyAriaAndText(): string {
    const points = this.order().loyaltyPointsToGain;
    if (points > 0) {
      return this.transloco.translate('modals.cart.loyalty_indicator.guest_points', { points });
    } else {
      return this.transloco.translate('modals.cart.loyalty_indicator.start_earning_points');
    }
  }
}
